import { useState } from "react";
import { CheckCircle2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatError } from "@/lib/api";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block font-mono text-xs uppercase tracking-widest text-[#a3b1aa]">{label}</span>
    {children}
  </label>
);

const EMPTY = { name: "", email: "", phone: "", message: "", consent: false };

export const ConsultForm = ({ results, overall }) => {
  const [form, setForm] = useState(EMPTY);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    setError("");
    try {
      await api.post("/softskills/consult", {
        ...form, overall, results: results.map((r) => ({ key: r.key, name: r.name, score: r.score })),
      });
      setState("sent");
    } catch (err) {
      setError(formatError(err));
      setState("idle");
    }
  };

  if (state === "sent") {
    return (
      <div data-testid="consult-success" className="flex items-start gap-4 border border-[#98bfa2] bg-[#0b120e] p-6 md:p-8">
        <CheckCircle2 className="mt-1 shrink-0 text-[#98bfa2]" size={28} />
        <div>
          <h3 className="font-display text-3xl font-900 uppercase leading-none">Risultati inviati.</h3>
          <p className="mt-2 text-sm text-[#a3b1aa]">Grazie {form.name}! Ti ho mandato un'email di conferma e ti ricontatterò io per la consulenza.</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="consult-form" className="space-y-5 border border-[#98bfa2] bg-[#0b120e] p-6 md:p-8">
      <div>
        <p className="eyebrow mb-3">Consulenza gratuita</p>
        <h3 className="font-display text-3xl font-900 uppercase leading-[0.95] md:text-5xl">Vuoi inviarmi i tuoi risultati?</h3>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#a3b1aa] md:text-base">
          Li guardo io, personalmente, e ti ricontatto per una consulenza: capiamo insieme da dove partire. È facoltativo, senza impegno.
        </p>
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        <Field label="Nome"><Input data-testid="consult-name-input" required minLength={2} maxLength={80} value={form.name} onChange={(e) => set("name", e.target.value)} className="h-12" /></Field>
        <Field label="Email"><Input data-testid="consult-email-input" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="h-12" /></Field>
      </div>
      <Field label="Telefono (facoltativo)"><Input data-testid="consult-phone-input" type="tel" maxLength={25} pattern="[0-9+()\s.-]+" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="h-12" /></Field>
      <Field label="Vuoi aggiungere qualcosa? (facoltativo)"><Textarea data-testid="consult-message-input" maxLength={1000} rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} /></Field>
      <label className="flex items-start gap-3 text-sm text-[#a3b1aa]">
        <Checkbox data-testid="consult-consent-checkbox" checked={form.consent} onCheckedChange={(v) => set("consent", v === true)} className="mt-0.5" />
        Acconsento all'invio dei miei risultati e dei miei dati a Hybrid Health Lab per essere ricontattato/a per una consulenza.
      </label>
      {error && <p data-testid="consult-error" className="text-sm text-[#f0a78f]">{error}</p>}
      <button data-testid="consult-submit-btn" disabled={state === "sending" || !form.consent}
        className="flex w-full items-center justify-center gap-2 bg-[#98bfa2] px-7 py-4 font-display text-lg font-800 uppercase text-[#050807] transition-colors hover:bg-[#c4dcca] disabled:opacity-50 md:w-auto">
        <Send size={18} /> {state === "sending" ? "Invio in corso…" : "Invia i risultati"}
      </button>
    </form>
  );
};
